import { useEffect, useState } from 'react'
import cn from 'classnames'
import { Form } from 'antd'
import { FormInstance } from 'antd/lib/form/hooks/useForm'
import { Input } from 'components/Input'
import { Select } from 'components/Select'
import { TOption } from 'components/Select/types'
import { LABELS, PLACEHOLDERS } from 'constants/txt'
import { fetchStateOptions } from 'features/PaymentDetails/api'
import { COUNTRIES_LIST } from '../../constants'
import { getCanadianPostalCodeItemProps } from './utils'

type AddressField = 'country' | 'street' | 'city' | 'state' | 'zip_code'

export type VendorAddressProps = {
  form: FormInstance
  namePath: (string | number)[]
  formPath?: (string | number)[]
  errorPath: (string | number)[]
  disabled?: boolean
  isDuplicate?: boolean
  allowClearState?: boolean
  streetClassName?: string
  className?: string
  getVendorError: (query: string[]) => string | undefined
}

let stateOptionsRequest: Promise<TOption[]> | null = null
let provinceOptionsRequest: Promise<TOption[]> | null = null

const loadRegionOptions = (country: 'US' | 'CA') => {
  const cachedRequest =
    country === 'US' ? stateOptionsRequest : provinceOptionsRequest
  if (cachedRequest) return cachedRequest

  const request = fetchStateOptions(country)
    .then((res) =>
      res.data.results.map((option) => ({
        label: option.id,
        value: option.id
      }))
    )
    .catch((error) => {
      if (country === 'US') stateOptionsRequest = null
      else provinceOptionsRequest = null
      throw error
    })

  if (country === 'US') stateOptionsRequest = request
  else provinceOptionsRequest = request

  return request
}

const useRegionOptions = () => {
  const [stateOptions, setStateOptions] = useState<TOption[]>([])
  const [provinceOptions, setProvinceOptions] = useState<TOption[]>([])

  useEffect(() => {
    let active = true

    loadRegionOptions('US')
      .then((options) => {
        if (active) setStateOptions(options)
      })
      .catch((error) => console.error(error))

    loadRegionOptions('CA')
      .then((options) => {
        if (active) setProvinceOptions(options)
      })
      .catch((error) => console.error(error))

    return () => {
      active = false
    }
  }, [])

  return { stateOptions, provinceOptions }
}

const VendorAddress = ({
  form,
  namePath,
  formPath = namePath,
  errorPath,
  disabled,
  isDuplicate,
  allowClearState,
  streetClassName,
  className,
  getVendorError
}: VendorAddressProps) => {
  const country = Form.useWatch([...formPath, 'country'], form)
  const { stateOptions, provinceOptions } = useRegionOptions()
  const isCanada = country === 'CA'

  const clearStateAndZip = () => {
    form.setFields([
      { name: [...formPath, 'state'], value: undefined, errors: [] },
      { name: [...formPath, 'zip_code'], value: undefined, errors: [] }
    ])
  }

  const fieldError = (field: AddressField) =>
    getVendorError([...(errorPath as string[]), field])

  const validationStatus = (field: AddressField) =>
    fieldError(field) || isDuplicate ? 'error' : undefined

  return (
    <div className={cn('column gap-24 mt-24', className)}>
      <div className="row">
        <Select
          options={COUNTRIES_LIST}
          className="country-field"
          propsItem={{
            label: LABELS.COUNTRY,
            name: [...namePath, 'country'],
            initialValue: COUNTRIES_LIST[0].value,
            help: fieldError('country'),
            validateStatus: validationStatus('country')
          }}
          propsSelect={{
            placeholder: PLACEHOLDERS.PLEASE_SELECT,
            disabled,
            onChange: clearStateAndZip
          }}
        />
      </div>
      <div className="row">
        <Input
          className={streetClassName}
          propsItem={{
            name: [...namePath, 'street'],
            label: LABELS.STREET,
            help: fieldError('street'),
            validateStatus: validationStatus('street')
          }}
          propsInput={{
            disabled
          }}
        />
      </div>
      <div className="row gap-24">
        <Input
          className="city-field"
          propsItem={{
            name: [...namePath, 'city'],
            label: LABELS.CITY,
            help: fieldError('city'),
            validateStatus: validationStatus('city')
          }}
          propsInput={{
            disabled
          }}
        />
        <Select
          options={isCanada ? provinceOptions : stateOptions}
          className="state-field"
          propsItem={{
            label: isCanada ? LABELS.PROVINCE_TERRITORY : LABELS.STATE,
            name: [...namePath, 'state'],
            help: fieldError('state'),
            validateStatus: validationStatus('state')
          }}
          propsSelect={{
            placeholder: PLACEHOLDERS.PLEASE_SELECT,
            disabled,
            allowClear: allowClearState
          }}
        />
        <Input
          className="zip-code-field"
          propsItem={{
            name: [...namePath, 'zip_code'],
            label: isCanada ? LABELS.POSTAL_CODE : LABELS.ZIP_CODE,
            help: fieldError('zip_code'),
            validateStatus: validationStatus('zip_code'),
            ...getCanadianPostalCodeItemProps(country)
          }}
          propsInput={{
            disabled,
            maxLength: isCanada ? 7 : undefined
          }}
        />
      </div>
    </div>
  )
}

export default VendorAddress
